import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  GRUPPEN_BESCHRIFTUNG,
  GRUPPEN_ICON,
  absoluterPfad,
  aktiveEintraege,
  baum,
  type Artgruppe,
  type Baumknoten,
  type Gruppe,
  type NavigationsEintrag,
} from '../navigation/struktur';
import { IconEinklappen, type IconProps } from '../Icons';
import {
  ladeZugeklappteGruppen, speichereZugeklappteGruppen,
  ladeOffeneGewerke, speichereOffeneGewerke,
} from './eingeklappt';
import { useEingeklappt, useSchmaleLeiste, setzeEingeklappt } from './schmal';
import './rahmen.css';

/**
 * Linke Hauptnavigation - zweistufig nach GEWERKEN (SL2).
 *
 * ═══ WARUM ZWEI EBENEN ══════════════════════════════════════════════════
 *
 * Bis zum 20.09.2026 war die Leiste flach und nach TAETIGKEIT gruppiert.
 * Die Lueftung lag dadurch in fuenf Gruppen (Fachplanung, Berechnungen,
 * Dokumentation, Koordination, Standards), und wer den Druckverlust suchte,
 * musste wissen, dass er eine "Berechnung" ist und keine "Fachplanung".
 * Jetzt steht alles, was zu einem Gewerk gehoert, unter diesem Gewerk -
 * sortiert nach Art (Planung, Berechnungen, Daten, Pruefung), bei Elektro
 * nach den Abschnitten aus `abschnitte.ts`.
 *
 * Offen ist nur, wo man steht (Akkordeon). Alles auf einmal waeren ueber
 * siebzig Zeilen in einer Spalte von 248 px - laenger als jedes Fenster.
 *
 * ═══ DIE ZEILE DES GEWERKS IST DER VERWEIS ══════════════════════════════
 *
 * Ein Gewerk traegt den Pfad seiner Modulseite. Deshalb ist seine Zeile ein
 * VERWEIS dorthin und daneben steht ein eigener Knopf zum Auf- und
 * Zuklappen. Es gibt darunter keine zusaetzliche Zeile "Uebersicht": jede
 * Route steht genau einmal im Baum, und `struktur.test.ts` prueft das.
 *
 * ═══ WAS OFFEN IST ══════════════════════════════════════════════════════
 *
 * `offen = von Hand geoeffnet ODER man steht darin`. Das Gewerk, in dem man
 * steht, laesst sich nicht zuklappen - sonst stuende man auf einer Seite,
 * deren Eintrag nirgends zu sehen ist. Dieselbe Ueberlegung galt schon fuer
 * die Gruppen; siehe `eingeklappt.ts`.
 *
 * Die Sidebar liest ausschliesslich `navigation/struktur.ts`. Sie enthaelt
 * keine eigene Liste von Zielen.
 */
export interface SidebarProps {
  eintraege: NavigationsEintrag[];
  projektId?: string;
}

export function Sidebar({ eintraege, projektId }: SidebarProps) {
  const eingeklappt = useEingeklappt();
  const schmal = useSchmaleLeiste();
  const [zuGruppen, setZuGruppen] = useState<string[]>(() => ladeZugeklappteGruppen());
  const [offeneGewerke, setOffeneGewerke] = useState<string[]>(() => ladeOffeneGewerke());
  const { pathname } = useLocation();

  const gruppen = useMemo(() => baum(eintraege), [eintraege]);

  /*
   * Die Kette vom Gewerk bis zur offenen Seite.
   *
   * ZWEI verschiedene Zustaende, und sie duerfen nicht verwechselt werden:
   * `aktiv` ist die ganze Kette (das Gewerk bekommt davon seine Spur und
   * bleibt offen), `aktuell` ist NUR das Blatt - `aria-current="page"` darf
   * genau einmal auf der Seite stehen. Beim ersten Lauf am 20.09.2026 trug
   * die Gewerkszeile "Lüftung" das `aria-current`, obwohl "Druckverlust"
   * offen war.
   */
  const kette = useMemo(
    () => aktiveEintraege(eintraege, projektId, pathname),
    [eintraege, projektId, pathname],
  );
  const aktiv = useMemo(() => new Set(kette.map((e) => e.schluessel)), [kette]);
  const aktuell = kette.length > 0 ? kette[kette.length - 1].schluessel : null;

  // Welche Gruppe traegt gerade die geoeffnete Seite? Sie wird immer gezeigt,
  // auch wenn der Benutzer sie einmal zugeklappt hat.
  const aktiveGruppe = useMemo<Gruppe | null>(() => {
    for (const { gruppe, knoten } of gruppen) {
      const trifft = knoten.some(
        (k) => aktiv.has(k.eintrag.schluessel)
          || k.arten.some((a) => a.eintraege.some((e) => aktiv.has(e.schluessel))),
      );
      if (trifft) return gruppe;
    }
    return null;
  }, [gruppen, aktiv]);

  // "Schmal" (von Hand eingeklappt ODER enges Fenster) steht in
  // `schmal.ts` und nicht hier: seit SL7 haengt auch der innere
  // Gliederungsbaum des Gewerks Elektro daran.

  /*
   * Den aktiven Eintrag in den sichtbaren Bereich holen. `block: 'nearest'`
   * statt `'center'`: Steht der Eintrag ohnehin im Bild, rollt nichts.
   */
  const liste = useRef<HTMLDivElement>(null);

  useEffect(() => {
    liste.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [pathname, eintraege]);

  function umschalten() {
    setzeEingeklappt(!eingeklappt);
  }

  function gruppeUmschalten(gruppe: Gruppe) {
    setZuGruppen((vorher) => {
      const neu = vorher.includes(gruppe)
        ? vorher.filter((g) => g !== gruppe)
        : [...vorher, gruppe];
      speichereZugeklappteGruppen(neu);
      return neu;
    });
  }

  function gewerkSetzen(schluessel: string, offen: boolean) {
    setOffeneGewerke((vorher) => {
      const neu = offen
        ? (vorher.includes(schluessel) ? vorher : [...vorher, schluessel])
        : vorher.filter((s) => s !== schluessel);
      speichereOffeneGewerke(neu);
      return neu;
    });
  }

  return (
    <nav
      className={`seitenleiste${schmal ? ' eingeklappt' : ''}`}
      aria-label="Hauptnavigation"
    >
      <div className="seitenleiste-liste" ref={liste}>
        {gruppen.map(({ gruppe, knoten }) => {
          const zu = zuGruppen.includes(gruppe) && gruppe !== aktiveGruppe;
          const titel = GRUPPEN_BESCHRIFTUNG[gruppe];
          const GruppenIcon = GRUPPEN_ICON[gruppe];

          return (
            <div className="seitenleiste-gruppe" key={gruppe}>
              <button
                type="button"
                className="seitenleiste-gruppe-titel"
                aria-expanded={!zu}
                onClick={() => gruppeUmschalten(gruppe)}
                title={schmal ? titel : undefined}
              >
                {GruppenIcon && <GruppenIcon size={14} />}
                <span className="seitenleiste-gruppe-text">{titel}</span>
                <span className="seitenleiste-gruppe-pfeil" aria-hidden="true">
                  {zu ? '▸' : '▾'}
                </span>
              </button>

              {!zu && knoten.map((k) => (
                <Knoten
                  key={k.eintrag.schluessel}
                  knoten={k}
                  projektId={projektId}
                  aktiv={aktiv}
                  aktuell={aktuell}
                  schmal={schmal}
                  offen={
                    aktiv.has(k.eintrag.schluessel)
                    || offeneGewerke.includes(k.eintrag.schluessel)
                  }
                  setzen={gewerkSetzen}
                />
              ))}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="seitenleiste-einklappen"
        onClick={umschalten}
        aria-pressed={eingeklappt}
        aria-label={eingeklappt ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
        title={eingeklappt ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
      >
        <IconEinklappen size={16} />
        <span className="seitenleiste-eintrag-text">
          {eingeklappt ? 'Ausklappen' : 'Einklappen'}
        </span>
      </button>
    </nav>
  );
}

/**
 * Ein Knoten erster Ebene: Gewerk oder Modulseite.
 *
 * In der schmalen Leiste bleibt nur sein Sinnbild stehen; der Klick fuehrt
 * auf die Uebersicht des Gewerks, den Namen traegt der Tooltip. Die
 * Unterseiten entfallen dort ganz - zwanzig gleiche Symbole untereinander
 * waeren keine Navigation.
 */
function Knoten({
  knoten, projektId, aktiv, aktuell, schmal, offen, setzen,
}: {
  knoten: Baumknoten;
  projektId?: string;
  aktiv: Set<string>;
  aktuell: string | null;
  schmal: boolean;
  offen: boolean;
  setzen: (schluessel: string, offen: boolean) => void;
}) {
  const { eintrag, arten } = knoten;
  const hatKinder = arten.length > 0;
  // Das Gewerk, in dem man steht, bleibt offen - es laesst sich nicht
  // zuklappen (siehe Kopfkommentar).
  const festOffen = aktiv.has(eintrag.schluessel);

  return (
    <div className={`seitenleiste-knoten${offen ? ' offen' : ''}`}>
      <div className="seitenleiste-knoten-kopf">
        <Eintragszeile
          eintrag={eintrag}
          projektId={projektId}
          inSpur={aktiv.has(eintrag.schluessel)}
          /*
           * In der SCHMALEN Leiste stehen nur die Gewerke. Der Eintrag der
           * offenen Seite ist dort gar nicht sichtbar - also uebernimmt das
           * Gewerk seine Markierung, sonst ist bei 1024 px kein einziger
           * Punkt markiert und die Frage "wo bin ich" unbeantwortet
           * (gemessen am 20.09.2026 an allen vier Unterseiten-Szenen).
           */
          aktuell={
            aktuell === eintrag.schluessel
            || (schmal && aktiv.has(eintrag.schluessel))
          }
          schmal={schmal}
          unterpunkt={false}
        />

        {hatKinder && !schmal && (
          <button
            type="button"
            className="seitenleiste-gewerk-pfeil"
            data-gewerk={eintrag.schluessel}
            aria-expanded={offen}
            aria-label={`${eintrag.beschriftung} ${offen ? 'zuklappen' : 'aufklappen'}`}
            title={`${eintrag.beschriftung} ${offen ? 'zuklappen' : 'aufklappen'}`}
            disabled={festOffen}
            onClick={() => setzen(eintrag.schluessel, !offen)}
            onKeyDown={(ereignis) => {
              // Pfeil rechts oeffnet, Pfeil links schliesst - das erwartete
              // Verhalten einer Baumnavigation. Enter/Leertaste loest den
              // Knopf ohnehin aus.
              if (ereignis.key === 'ArrowRight') {
                ereignis.preventDefault();
                setzen(eintrag.schluessel, true);
              }
              if (ereignis.key === 'ArrowLeft') {
                ereignis.preventDefault();
                setzen(eintrag.schluessel, false);
              }
            }}
          >
            <span aria-hidden="true">{offen ? '▾' : '▸'}</span>
          </button>
        )}
      </div>

      {hatKinder && offen && !schmal && (
        <div className="seitenleiste-unterbaum">
          {arten.map((art) => (
            <Artabschnitt
              key={art.art}
              art={art}
              projektId={projektId}
              aktiv={aktiv}
              aktuell={aktuell}
              schmal={schmal}
              gewerkIcon={eintrag.Icon}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Eine Zwischenzeile (Planung, Berechnungen, ...) mit ihren Seiten. */
function Artabschnitt({
  art, projektId, aktiv, aktuell, schmal, gewerkIcon,
}: {
  art: Artgruppe;
  projektId?: string;
  aktiv: Set<string>;
  aktuell: string | null;
  schmal: boolean;
  /** Sinnbild des Gewerks - Kinder mit demselben zeigen keins. */
  gewerkIcon: ComponentType<IconProps>;
}) {
  return (
    <>
      {art.beschriftung !== '' && (
        <div className="seitenleiste-art">{art.beschriftung}</div>
      )}
      {art.eintraege.map((e) => (
        <Eintragszeile
          key={e.schluessel}
          eintrag={e}
          projektId={projektId}
          inSpur={aktiv.has(e.schluessel)}
          aktuell={aktuell === e.schluessel}
          schmal={schmal}
          unterpunkt
          ohneIcon={e.Icon === gewerkIcon}
        />
      ))}
    </>
  );
}

function Eintragszeile({
  eintrag, projektId, inSpur, aktuell, schmal, unterpunkt, ohneIcon = false,
}: {
  eintrag: NavigationsEintrag;
  projektId?: string;
  /** Der Eintrag liegt auf dem Weg zur offenen Seite (Gewerk). */
  inSpur: boolean;
  /** Der Eintrag IST die offene Seite. Nur er traegt `aria-current`. */
  aktuell: boolean;
  schmal: boolean;
  unterpunkt: boolean;
  /**
   * Das Sinnbild entfaellt, weil es dasselbe ist wie am Gewerk darueber.
   *
   * Unter Elektro stand zwoelfmal derselbe Blitz untereinander - er
   * unterschied nichts und trug nichts bei; was die Zeilen unterscheidet,
   * ist ihr Text. Der Platz bleibt trotzdem stehen, damit die Texte der
   * Geschwister fluchten, auch wenn eines ein eigenes Sinnbild hat.
   */
  ohneIcon?: boolean;
}) {
  const ziel = absoluterPfad(eintrag, projektId);
  const beschriftung = eintrag.marke
    ? `${eintrag.beschriftung} (${eintrag.marke})`
    : eintrag.beschriftung;

  return (
    <Link
      to={ziel}
      /* Im eingeklappten Zustand ist der `title` der einzige Weg zur
         Beschriftung; ausgeklappt traegt er die Erlaeuterung des
         Fachbereichs, falls der Server eine liefert. */
      title={schmal ? beschriftung : (eintrag.beschreibung ?? beschriftung)}
      aria-label={schmal ? beschriftung : undefined}
      className={[
        'seitenleiste-eintrag',
        unterpunkt ? 'unterpunkt' : 'knoten',
        aktuell ? 'aktiv' : '',
        inSpur && !aktuell ? 'spur' : '',
      ].filter(Boolean).join(' ')}
      aria-current={aktuell ? 'page' : undefined}
    >
      {ohneIcon
        ? <span className="seitenleiste-platzhalter" aria-hidden="true" />
        : <eintrag.Icon size={16} />}
      <span className="seitenleiste-eintrag-text">{eintrag.beschriftung}</span>
      {eintrag.marke && (
        <span className="seitenleiste-marke">{eintrag.marke}</span>
      )}
    </Link>
  );
}
